import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Heart, MessageCircle, Repeat2, Bookmark, Star,
  Clock, Users, CheckCircle, Flame, Send, Trash2, Loader2, Share2
} from 'lucide-react';
import {
  foodCommunityService, RecipePost, RecipeComment,
  getTagLabel, getTagColor, formatTimeAgo
} from '../services/foodCommunityService';

const RecipePostDetailPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRepost = searchParams.get('repost') === '1';

  const [post, setPost] = useState<RecipePost | null>(null);
  const [comments, setComments] = useState<RecipeComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [showRepostModal, setShowRepostModal] = useState(isRepost);
  const [repostNote, setRepostNote] = useState('');
  const [reposting, setReposting] = useState(false);
  const commentInputRef = useRef<HTMLInputElement>(null);

  const currentUserId = (() => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id || payload.sub || null;
    } catch { return null; }
  })();

  useEffect(() => {
    if (!postId) return;
    loadPost();
    loadComments();
  }, [postId]);

  const loadPost = async () => {
    try {
      const p = await foodCommunityService.getPost(postId!);
      setPost(p);
    } catch {
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    setCommentsLoading(true);
    try {
      const res = await foodCommunityService.getComments(postId!);
      setComments(res.comments);
    } catch {}
    finally { setCommentsLoading(false); }
  };

  const handleLike = async () => {
    if (!post) return;
    const prev = { ...post };
    setPost(p => p ? {
      ...p,
      isLiked: !p.isLiked,
      likesCount: p.isLiked ? p.likesCount - 1 : p.likesCount + 1,
    } : p);
    try {
      await foodCommunityService.toggleLike(post.id);
    } catch { setPost(prev); }
  };

  const handleSave = async () => {
    if (!post) return;
    const prev = { ...post };
    setPost(p => p ? {
      ...p,
      isSaved: !p.isSaved,
      savesCount: p.isSaved ? p.savesCount - 1 : p.savesCount + 1,
    } : p);
    try {
      await foodCommunityService.toggleSave(post.id);
    } catch { setPost(prev); }
  };

  const handleRate = async (rating: number) => {
    if (!post) return;
    try {
      const result = await foodCommunityService.ratePost(post.id, rating);
      setPost(p => p ? {
        ...p,
        userRating: rating,
        averageRating: result.averageRating,
        ratingsCount: p.userRating ? p.ratingsCount : p.ratingsCount + 1,
      } : p);
      setShowRating(false);
    } catch {}
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || submittingComment) return;
    setSubmittingComment(true);
    try {
      const comment = await foodCommunityService.addComment(postId!, commentText.trim());
      setComments(prev => [...prev, comment]);
      setCommentText('');
      setPost(p => p ? { ...p, commentsCount: p.commentsCount + 1 } : p);
    } catch {}
    finally { setSubmittingComment(false); }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await foodCommunityService.deleteComment(commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setPost(p => p ? { ...p, commentsCount: Math.max(0, p.commentsCount - 1) } : p);
    } catch {}
  };

  const handleCommentLike = async (commentId: string) => {
    setComments(prev => prev.map(c =>
      c.id === commentId
        ? { ...c, isLiked: !c.isLiked, likesCount: c.isLiked ? c.likesCount - 1 : c.likesCount + 1 }
        : c
    ));
    try {
      await foodCommunityService.toggleCommentLike(commentId);
    } catch {
      setComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, isLiked: !c.isLiked, likesCount: c.isLiked ? c.likesCount - 1 : c.likesCount + 1 }
          : c
      ));
    }
  };

  const handleRepost = async () => {
    if (!post || reposting) return;
    setReposting(true);
    try {
      await foodCommunityService.repost(post.id, undefined, repostNote || undefined);
      setPost(p => p ? { ...p, repostsCount: p.repostsCount + 1 } : p);
      setShowRepostModal(false);
      setRepostNote('');
    } catch {}
    finally { setReposting(false); }
  };

  const handleShare = async () => {
    if (!post) return;
    const url = `${window.location.origin}/food-community/post/${post.id}`;
    if (navigator.share) {
      await navigator.share({ title: post.title, url }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(url).catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) return null;

  const userName = post.user
    ? `${post.user.firstName || ''} ${post.user.lastName || ''}`.trim()
    : 'Utilizator';
  const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const allTags = [...post.tags, ...post.customTags];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </button>
          <h1 className="font-bold text-gray-900 text-sm truncate max-w-[200px]">{post.title}</h1>
          <button
            onClick={handleShare}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <Share2 size={17} className="text-gray-600" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto pb-32">
        {/* Image */}
        {post.imageUrl && (
          <div className="relative overflow-hidden bg-gray-100" style={{ maxHeight: 380 }}>
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full object-cover"
              style={{ maxHeight: 380 }}
            />
            {post.calories && (
              <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 text-white text-sm font-bold px-3 py-1.5 rounded-full">
                <Flame size={14} className="text-orange-400" />
                {Math.round(post.calories)} kcal
              </div>
            )}
          </div>
        )}

        <div className="px-4 pt-4 space-y-4">
          {/* Author */}
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate(`/food-community/user/${post.userId}`)}
            >
              {post.user?.profilePhotoUrl ? (
                <img src={post.user.profilePhotoUrl} alt={userName} className="w-11 h-11 rounded-full object-cover ring-2 ring-gray-100" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white font-semibold">
                  {initials}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{userName}</span>
                  {post.user?.userType === 'SPECIALIST' && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Nutritionist</span>
                  )}
                </div>
                <span className="text-xs text-gray-400">{formatTimeAgo(post.createdAt)}</span>
              </div>
            </div>
            {post.isValidatedByNutritionist && (
              <span className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-full font-medium">
                <CheckCircle size={12} />
                Validat
              </span>
            )}
          </div>

          {/* Title & Description */}
          <div>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">{post.title}</h1>
            {post.description && (
              <p className="text-gray-600 text-sm mt-2 leading-relaxed">{post.description}</p>
            )}
          </div>

          {/* Tags */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <span key={tag} className={`text-xs px-2.5 py-1 rounded-full font-medium ${getTagColor(tag)}`}>
                  {getTagLabel(tag)}
                </span>
              ))}
            </div>
          )}

          {/* Macros */}
          {(post.protein != null || post.carbs != null || post.fats != null || post.prepTime) && (
            <div className="bg-gray-50 rounded-2xl p-4 grid grid-cols-4 gap-3">
              {post.protein != null && (
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">{Math.round(post.protein)}g</div>
                  <div className="text-xs text-gray-400 mt-0.5">Proteine</div>
                </div>
              )}
              {post.carbs != null && (
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">{Math.round(post.carbs)}g</div>
                  <div className="text-xs text-gray-400 mt-0.5">Carbo</div>
                </div>
              )}
              {post.fats != null && (
                <div className="text-center">
                  <div className="text-lg font-bold text-amber-600">{Math.round(post.fats)}g</div>
                  <div className="text-xs text-gray-400 mt-0.5">Grasimi</div>
                </div>
              )}
              {post.prepTime && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-lg font-bold text-gray-600">
                    <Clock size={15} />
                    {post.prepTime}m
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">Timp</div>
                </div>
              )}
            </div>
          )}

          {/* Rating */}
          <div className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star
                    key={s}
                    size={18}
                    className={s <= Math.round(post.averageRating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
                  />
                ))}
              </div>
              <span className="font-bold text-gray-800">{(post.averageRating || 0).toFixed(1)}</span>
              <span className="text-sm text-gray-400">({post.ratingsCount} recenzii)</span>
            </div>
            {!post.userRating && (
              <button
                onClick={() => setShowRating(!showRating)}
                className="text-sm text-green-600 font-semibold hover:text-green-700"
              >
                Evalueaza
              </button>
            )}
            {post.userRating && (
              <span className="text-xs text-green-600 font-medium">Ai evaluat cu {post.userRating} stele</span>
            )}
          </div>

          {showRating && (
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Cum ti s-a parut reteta?</p>
              <div className="flex items-center gap-2 justify-center">
                {[1, 2, 3, 4, 5].map(s => (
                  <button
                    key={s}
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => handleRate(s)}
                  >
                    <Star
                      size={32}
                      className={s <= (hoverRating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 px-4 py-3">
            <div className="flex items-center gap-5">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${post.isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-400'}`}
              >
                <Heart size={20} className={post.isLiked ? 'fill-red-500' : ''} />
                <span>{post.likesCount}</span>
              </button>
              <button
                onClick={() => commentInputRef.current?.focus()}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-blue-500 transition-colors"
              >
                <MessageCircle size={20} />
                <span>{post.commentsCount}</span>
              </button>
              <button
                onClick={() => setShowRepostModal(true)}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-green-500 transition-colors"
              >
                <Repeat2 size={20} />
                <span>{post.repostsCount}</span>
              </button>
            </div>
            <button
              onClick={handleSave}
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${post.isSaved ? 'text-green-600' : 'text-gray-500 hover:text-green-500'}`}
            >
              <Bookmark size={20} className={post.isSaved ? 'fill-green-600' : ''} />
              <span className="text-xs">{post.isSaved ? 'Salvat' : 'Salveaza'}</span>
            </button>
          </div>

          {/* Ingredients */}
          {post.ingredients && post.ingredients.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900">Ingrediente</h3>
                {post.servings && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Users size={13} />
                    {post.servings} portii
                  </span>
                )}
              </div>
              <ul className="space-y-2">
                {post.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0">
                    <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 flex-1">{ing.name}</span>
                    <span className="text-sm font-semibold text-gray-500">{ing.amount} {ing.unit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Instructions */}
          {post.instructions && (
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-bold text-gray-900 mb-3">Mod de preparare</h3>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{post.instructions}</p>
            </div>
          )}

          {/* Comments */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-50">
              <h3 className="font-bold text-gray-900">Comentarii ({post.commentsCount})</h3>
            </div>

            {commentsLoading ? (
              <div className="flex justify-center py-6">
                <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Fii primul care comenteaza!
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {comments.map(comment => {
                  const cName = `${comment.user?.firstName || ''} ${comment.user?.lastName || ''}`.trim();
                  const cInitials = cName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                  const isOwn = comment.userId === currentUserId;

                  return (
                    <div key={comment.id} className="px-4 py-3 flex gap-3">
                      {comment.user?.profilePhotoUrl ? (
                        <img src={comment.user.profilePhotoUrl} alt={cName} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                          {cInitials}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-800">{cName}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{formatTimeAgo(comment.createdAt)}</span>
                            {isOwn && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-gray-300 hover:text-red-400 transition-colors"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{comment.content}</p>
                        <button
                          onClick={() => handleCommentLike(comment.id)}
                          className={`flex items-center gap-1 mt-1 text-xs transition-colors ${comment.isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                        >
                          <Heart size={12} className={comment.isLiked ? 'fill-red-500' : ''} />
                          {comment.likesCount > 0 && <span>{comment.likesCount}</span>}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comment Input - Fixed Bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 z-20" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <input
            ref={commentInputRef}
            type="text"
            placeholder="Adauga un comentariu..."
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddComment()}
            onFocus={() => window.dispatchEvent(new CustomEvent('mobile-drawer-toggle', { detail: { open: true } }))}
            onBlur={() => window.dispatchEvent(new CustomEvent('mobile-drawer-toggle', { detail: { open: false } }))}
            className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-green-400"
          />
          <button
            onClick={handleAddComment}
            disabled={!commentText.trim() || submittingComment}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              commentText.trim() ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-400'
            }`}
          >
            {submittingComment ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>

      {/* Repost Modal */}
      {showRepostModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-white rounded-t-3xl w-full max-w-2xl p-6">
            <h3 className="font-bold text-gray-900 text-lg mb-1">Am gatit si eu asta</h3>
            <p className="text-sm text-gray-500 mb-4">Reposta reteta cu propria ta nota</p>
            <textarea
              placeholder="Adauga o nota (optional)..."
              value={repostNote}
              onChange={e => setRepostNote(e.target.value)}
              rows={3}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-green-400 resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowRepostModal(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Anuleaza
              </button>
              <button
                onClick={handleRepost}
                disabled={reposting}
                className="flex-1 py-3 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
              >
                {reposting ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Reposteaza'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipePostDetailPage;
